import React, { createContext, useState } from "react";
import PropTypes from "prop-types";
import createColorScale from "../utils/createColorScale";
import {
  SCALE_OPTIONS,
  LEGEND_OPTIONS,
  INITIAL_YEAR_OPTION,
  INITIAL_MAP_KEY_OPTION,
  DEFAULT_SELECTION,
  DUMMY_SELECTION_POTTER,
} from "../utils/constants";

export const DataContext = createContext();

const DataContextProvider = ({ children, data }) => {
  // FILTERS
  const [year, setYear] = useState(INITIAL_YEAR_OPTION.value);
  const [choroplethColorKey, setChoroplethColorKey] = useState(
    INITIAL_MAP_KEY_OPTION.value
  );

  // COMPUTED VALS
  const [legendData, setLegendData] = useState(
    LEGEND_OPTIONS[choroplethColorKey]
  );
  const { judges: rawJudges, counties: rawCounties, geoJson } = data;
  const [judgesData, setJudgesData] = useState(rawJudges[year]);
  const [countyData, setCountyData] = useState(rawCounties[year]);
  const INITIAL_COLOR_SCALE = createColorScale({
    data: countyData,
    accessor: choroplethColorKey,
    ...SCALE_OPTIONS[choroplethColorKey],
  });
  const [colorScale, setColorScale] = useState(() => INITIAL_COLOR_SCALE);
  const [selection, setSelection] = useState(DEFAULT_SELECTION);

  // DIMENSIONS
  const [mapDims, setMapDims] = useState({
    width: 0,
    height: 0,
  });
  const [colorLegendDims, setColorLegendDims] = useState({
    width: 0,
    height: 0,
  });
  const [panelDims, setPanelDims] = useState({
    width: 0,
    height: 0,
  });

  // HANDLERS
  const changeYear = (newYear) => {
    setYear(newYear);
    const newCountyData = rawCounties[newYear];
    setJudgesData(rawJudges[newYear]);
    setCountyData(newCountyData);
  };

  const changeMapKey = (key) => {
    setChoroplethColorKey(key);
    const newColorScale = createColorScale({
      data: countyData,
      accessor: key,
      ...SCALE_OPTIONS[key],
    });
    setColorScale(() => newColorScale);
    setLegendData(LEGEND_OPTIONS[key]);
  };

  const changeSelection = (newSelection) => {
    if (newSelection && newSelection.id) {
      const { id, type, county } = newSelection;
      let selectedOption;
      let cleanCounty;
      if (type === "judge") {
        selectedOption = judgesData.find((option) => option.id === id);
        cleanCounty = county;
      } else {
        selectedOption = countyData.find((option) => option.id === id);
        cleanCounty = id;
      }
      setSelection({
        id,
        type,
        county: cleanCounty,
        label: selectedOption.label,
      });
    } else {
      setSelection(DEFAULT_SELECTION);
    }
  };

  return (
    <DataContext.Provider
      value={{
        year,
        setYear,
        judgesData,
        countyData,
        geoJson,
        selection,
        changeSelection,
        mapDims,
        setMapDims,
        colorScale,
        choroplethColorKey,
        changeYear,
        legendData,
        changeMapKey,
        colorLegendDims,
        setColorLegendDims,
        panelDims,
        setPanelDims,
      }}
    >
      {children}
    </DataContext.Provider>
  );
};

DataContextProvider.propTypes = {
  children: PropTypes.node.isRequired,
  data: PropTypes.objectOf(PropTypes.any).isRequired,
};

export default DataContextProvider;
